// skipcq: JS-W1028, JS-0128
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import DOMPurify from 'dompurify';

/**
 * HighlightedText
 * Safely renders text with HTML highlighting (typically from Typesense search results).
 * Typesense wraps matched text in <mark> tags.
 * HTML is sanitized using DOMPurify before rendering to prevent XSS attacks.
 */
function HighlightedText({ html, variant, component, ...otherProps }) {
  // Sanitize HTML using DOMPurify before rendering
  const sanitizedHtml = useMemo(() => {
    if (!html) return null;
    // Allow mark tags for highlighting, but sanitize everything else
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['mark'],
      ALLOWED_ATTR: [],
    });
  }, [html]);

  if (!sanitizedHtml) return null;

  return (
    <Typography
      variant={variant}
      component={component}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      sx={{
        '& mark': {
          backgroundColor: '#FFEB3B', // Yellow highlight
          color: 'inherit',
          fontWeight: 'bold',
          padding: '0 2px',
          borderRadius: '2px',
        },
      }}
      {...otherProps}
    />
  );
}

HighlightedText.propTypes = {
  html: PropTypes.string,
  variant: PropTypes.string,
  component: PropTypes.string,
};

HighlightedText.defaultProps = {
  html: null,
  variant: 'body1',
  component: 'span',
};

export default HighlightedText;
